function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo } from 'react';
import cn from 'classnames';
import { Badge, Callout, Tooltip, Typography } from 'seagull';
import { DollarCircle } from 'seagull/icons';
import { useResponsive } from "../../contexts";
import { useIoContext } from "../../../contexts";
import { useServicesBillingDetails } from "../../../hooks/reactQuery/queries/useServicesBillingDetails";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { relationshipModel } from "../../../models";
import { utils } from "../../../utils";
import { BillingGroupTypes, summarize } from "../genServicesSummary";
import styles from "./BillingSchedule.module.scss";
import { getBillingGroupTitle, processBillingGroups } from "./BillingSchedule.utils";
import BillingStageRow from "./BillingStageRow";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
function renderServiceRow(_ref) {
  var _servicesBillingDetai;
  let {
    variablePrice,
    name,
    price,
    minPrice,
    isRange,
    unit,
    prorated,
    hasBilledUpfront,
    groupType,
    serviceId,
    servicesBillingDetails,
    START_BILLING_AT
  } = _ref;
  const priorPeriods = ((_servicesBillingDetai = servicesBillingDetails[serviceId]) === null || _servicesBillingDetai === void 0 ? void 0 : _servicesBillingDetai.periods) || 0;
  const getServiceHeaderText = () => {
    if (variablePrice && !isRange) {
      return "".concat(name, " (").concat(utils.formatDollarAmount(price), "/").concat(unit || 'hour', ")");
    }
    if (isRange) {
      return "".concat(name, " (").concat(minPrice != null ? "".concat(utils.formatDollarAmount(minPrice), " - ") : 'up to ').concat(utils.formatDollarAmount(price), ")");
    }
    return "".concat(name, " ").concat(priorPeriods > 0 && START_BILLING_AT && groupType == BillingGroupTypes.Today ? "(".concat(priorPeriods, " prior periods)") : '');
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Typography, {
      "data-testid": "billing-schedule-service-".concat(name, "-").concat(groupType, "-").concat(variablePrice, "-").concat(priorPeriods),
      children: getServiceHeaderText()
    }), prorated && (groupType === BillingGroupTypes.Today || !hasBilledUpfront) && /*#__PURE__*/_jsx(Tooltip, {
      placement: "right",
      title: 'The final payment amount will be calculated based on the proposal approval date',
      children: /*#__PURE__*/_jsx(Badge, {
        rounded: true,
        outlined: true,
        skin: "warning",
        className: styles.billingScheduleListItemBadge,
        "data-testid": "billing-schedule-service-prorated-badge",
        children: "Prorated"
      })
    })]
  });
}
const BillingSchedule = _ref2 => {
  let {
    selectedOptionalServices,
    selectedBundleID,
    relationship
  } = _ref2;
  const {
    isMobile
  } = useResponsive();
  const {
    getNow
  } = useIoContext();
  const {
    START_BILLING_AT = false
  } = useFeatureFlags();
  const effectiveDate = relationshipModel.effectiveDate(relationship);
  const services = useMemo(() => relationshipModel.actualServices(relationship, selectedBundleID), [relationship, selectedBundleID]);
  const {
    servicesBillingDetails
  } = useServicesBillingDetails({
    services,
    relationshipEffectiveDate: effectiveDate
  });
  const servicesWithPriorPeriods = useMemo(() => {
    const map = {};
    services.forEach(service => {
      var _servicesBillingDetai2, _servicesBillingDetai3;
      const serviceId = service.id;
      const priorPeriods = (_servicesBillingDetai2 = (_servicesBillingDetai3 = servicesBillingDetails[serviceId]) === null || _servicesBillingDetai3 === void 0 ? void 0 : _servicesBillingDetai3.periods) !== null && _servicesBillingDetai2 !== void 0 ? _servicesBillingDetai2 : 0;
      map[serviceId] = priorPeriods > 0;
    });
    return map;
  }, [services, servicesBillingDetails]);
  const billingSummary = useMemo(() => services.reduce(summarize({
    selectedOptionalServices: selectedOptionalServices || [],
    effectiveDate,
    now: getNow(),
    START_BILLING_AT,
    servicesWithPriorPeriods
  }), {}), [services, selectedOptionalServices, effectiveDate, START_BILLING_AT, servicesWithPriorPeriods]);
  const serviceNameToIdMap = useMemo(() => {
    const map = {};
    services.forEach(service => {
      map[service.name] = service.id;
    });
    return map;
  }, [services]);
  const getGroups = () => {
    return processBillingGroups(billingSummary, serviceNameToIdMap, servicesBillingDetails, START_BILLING_AT, getNow(), effectiveDate || undefined);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      className: cn(styles.sectionTitle, {
        [styles.mobile]: isMobile
      }),
      children: [/*#__PURE__*/_jsx(DollarCircle, {}), /*#__PURE__*/_jsxs("div", {
        className: styles.sectionTitleContent,
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "h6",
          weight: "bold",
          children: "Billing Schedule"
        }), /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph1",
          weight: "light",
          skin: "secondary",
          children: "For automatically billed services"
        })]
      })]
    }), getGroups().map(_ref3 => {
      let {
        group,
        groupType,
        variablePrice,
        startDate,
        isPaused
      } = _ref3;
      return /*#__PURE__*/_jsxs("div", {
        className: styles.billingSchedule,
        children: [/*#__PURE__*/_jsxs(Typography, {
          variant: "paragraph1",
          weight: "bold",
          showWhiteSpaces: true,
          breakWords: true,
          className: cn(styles.coverLetter, {
            [styles.mobile]: isMobile
          }),
          "data-testid": "billing-schedule-group-title-".concat(groupType, "-").concat(variablePrice).concat(startDate ? "-".concat(startDate.toISOString().split('T')[0]) : ''),
          children: [getBillingGroupTitle(groupType, startDate, relationship, isPaused), !isPaused && /*#__PURE__*/_jsxs(_Fragment, {
            children: [' - ', !variablePrice ? utils.formatDollarAmount(group.totalAmount) : 'variable']
          })]
        }), groupType === BillingGroupTypes.Today && group.hasBilledUpfront && /*#__PURE__*/_jsx(Callout, {
          intent: "info",
          className: styles.billingScheduleListItem,
          children: "First invoice contains recurring services in an active billing period."
        }), group.services.map((service, index) => /*#__PURE__*/_jsx("ul", {
          className: cn(styles.listItem, styles.billingScheduleListItem),
          "data-testid": "billing-schedule-service",
          children: service.billingStages ? /*#__PURE__*/_jsx(BillingStageRow, {
            name: service.name,
            price: service.price,
            billingStages: service.billingStages
          }) : renderServiceRow(_objectSpread(_objectSpread({}, service), {}, {
            variablePrice,
            hasBilledUpfront: group.hasBilledUpfront,
            groupType,
            serviceId: serviceNameToIdMap[service.name] || '',
            servicesBillingDetails,
            START_BILLING_AT
          }))
        }, index))]
      }, "".concat(groupType, "-").concat(variablePrice, "-").concat(startDate === null || startDate === void 0 ? void 0 : startDate.toISOString()));
    })]
  });
};
export default BillingSchedule;